<template>
  <div>
    <Transition>
      <PopupBackdrop v-if="showBackdrop" />
    </Transition>
    <Transition @after-leave="showNextPopup">
      <GeneralPopup
        v-if="generalPopupItem"
        :heading="generalPopupItem.title"
        :body-markdown="generalPopupItem.body"
        :image-url="generalPopupItem.image_url"
        :button-url="generalPopupItem.url"
        :conversion-url="generalPopupItem.conversion_url"
        @close="onClose"
      />
      <ScoutPopup v-if="scoutPopupItems" :items="scoutPopupItems" @close="onClose" />
      <EventPopup v-if="eventPopupItems" :user-last-name="userLastName" :items="eventPopupItems" @close="onClose" />
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from '@vue/composition-api';
import PopupBackdrop from 'components/popup/PopupBackdrop.vue';
import GeneralPopup from 'containers/GeneralPopup.vue';
import ScoutPopup from 'containers/ScoutPopup.vue';
import EventPopup from 'containers/EventPopup.vue';
import { fetchPopup, readPopup, ScoutPopupItem, EventPopupItem } from 'oc2api/popup';
import type { PopupData } from 'oc2api/popup';

const useFetchPopup = () => {
  let nextPromise: Promise<PopupData | undefined> | undefined = undefined;

  const nextPopupData = (): Promise<PopupData | undefined> => {
    const promise = nextPromise;
    nextPromise = undefined;
    return promise || fetchPopup();
  };

  const prefetchPopup = () => {
    nextPromise = fetchPopup();
  };

  const popupFinished = async () => {
    if (nextPromise == null) return false;

    const popupData = await nextPromise;
    return popupData == null;
  };

  return { nextPopupData, prefetchPopup, popupFinished };
};

const usePopupControl = () => {
  const showBackdrop = ref(false);
  const showPopup = ref(false);
  const popupData = ref<PopupData>();

  const { nextPopupData, prefetchPopup } = useFetchPopup();

  const showPopupData = (data: PopupData | undefined) => {
    const popupExists = data != null;
    showBackdrop.value = popupExists;
    showPopup.value = popupExists;
    popupData.value = data;
  };

  const showNextPopup = async () => {
    const data = await nextPopupData();
    showPopupData(data);
    if (data) {
      await readPopup(data.popup.id);
      prefetchPopup();
    }
  };

  const onClose = async () => {
    showBackdrop.value = false;
    showPopup.value = false;
  };

  return {
    showBackdrop,
    showPopup,
    popupData,
    showNextPopup,
    onClose,
  };
};

export default defineComponent({
  name: 'OCPopup',
  components: {
    PopupBackdrop,
    GeneralPopup,
    ScoutPopup,
    EventPopup,
  },
  props: {
    userLastName: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { showBackdrop, showPopup, popupData, showNextPopup, onClose } = usePopupControl();

    const generalPopupItem = computed(() => {
      if (!showPopup.value) return undefined;
      if (popupData.value?.popup.type !== 'general') return undefined;

      return popupData.value.popup.item;
    });
    const scoutPopupItems = computed(() => {
      if (!showPopup.value) return undefined;
      if (popupData.value?.popup.type !== 'scouts') return undefined;

      return popupData.value.popup.items as ScoutPopupItem[];
    });
    const eventPopupItems = computed(() => {
      if (!showPopup.value) return undefined;
      if (popupData.value?.popup.type !== 'events') return undefined;

      return popupData.value.popup.items as EventPopupItem[];
    });

    showNextPopup();

    return {
      showBackdrop,
      generalPopupItem,
      scoutPopupItems,
      eventPopupItems,
      showNextPopup,
      onClose,
    };
  },
});
</script>

<style lang="scss" scoped>
.v-enter {
  opacity: 0;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.25s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
